'use client';

import type { FilePreviewData } from './FilePreview';
import type { UploadedFile } from '@/files/uploads';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/era-ui';
import { cn } from '@/libs/tailwind';
import { FilePreview } from './FilePreview';
import { FileThumbnail } from './FileThumbnail';
import { Toast } from './legacy/Toast';

export type FileUploadButtonProps = {
  files: UploadedFile[];
  onFileAdd: (file: File) => void;
  onFileRemove: (fileId: string) => void;
  className?: string;
  acceptedFileTypes?: string;
  disabled?: boolean;
};

// Allowed file types
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

// Maximum file size (2GB in bytes)
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

export function FileUploadButton({
  files,
  onFileAdd,
  onFileRemove,
  className,
  acceptedFileTypes = 'image/png,image/jpeg,image/jpg,application/pdf',
  disabled = false,
}: FileUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovering, setIsHovering] = useState<string | null>(null);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FilePreviewData | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const file = selectedFiles[0];
    if (file) {
      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setErrorToast('Only PNG, JPEG, and PDF files are allowed.');
        // Reset the input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setErrorToast('File size cannot exceed 2GB.');
        // Reset the input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      onFileAdd(file);
    }

    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    onFileRemove(fileId);
  };

  const handleOpenPreview = (file: UploadedFile) => {
    // Allow preview for both images and PDFs
    if (file.file.type.startsWith('image/') || file.file.type === 'application/pdf') {
      setPreviewFile({
        ...file,
        name: file.file.name,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'rounded-full h-8 w-8 bg-gray-200 text-gray-700 hover:bg-[#D3FB52] hover:text-black transition-colors',
            className,
          )}
          onClick={handleButtonClick}
          disabled={disabled}
        >
          <Plus className="h-4 w-4" />
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={acceptedFileTypes}
            onChange={handleFileChange}
          />
        </Button>
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-3">
          {files.map(file => (
            <FileThumbnail
              key={file.id}
              id={file.id}
              file={file.file}
              url={file.url}
              isHovering={isHovering === file.id}
              onRemove={handleRemoveFile}
              onClick={() => handleOpenPreview(file)}
              onHoverChange={(id, isHovering) => setIsHovering(isHovering ? id : null)}
            />
          ))}
        </div>
      )}

      {/* Use the FilePreview component */}
      <FilePreview
        file={previewFile}
        onClose={handleClosePreview}
      />

      {/* Error Toast Notification */}
      {errorToast && (
        <Toast
          message={errorToast}
          type="error"
          duration={5000}
          onClose={() => setErrorToast(null)}
        />
      )}
    </div>
  );
}
